import { useQueries, useSuspenseQuery } from '@tanstack/react-query';
import {
  FilterSection,
  HierarchyItem,
} from '../../common/components/filter/types';
import { searchItems } from '../../common/components/filter/utils';
import { MINIMUM_SEARCH_INPUT } from '../../constants';
import { getKeyFromDataFieldWithDataType } from '../../utils';
import { hashString, VersionId } from '../types';
import { DataService } from './service';
import { FilterDataManager } from './types';

export const useFilterValues = (filterDataManager: FilterDataManager) => {
  const queryKey = filterDataManager.queryKey;
  return useSuspenseQuery({
    queryKey,
    queryFn: async () => {
      const response = await filterDataManager.fetch();
      const result = await filterDataManager.process(response);
      return result;
    },
  });
};

export const useAllFilterValues = (
  searchText: string,
  filterSections: FilterSection[]
) => {
  const filterDataManagers = filterSections.map(
    (fs) => fs.filterDataManagerForFilterTray
  );
  const {
    data: queriesResult,
    error,
    isFetching,
  } = useQueries({
    queries: filterDataManagers.map((filterDataManager) => {
      return {
        queryKey: filterDataManager.queryKey,
        queryFn: async () => {
          const response = await filterDataManager.fetch();
          const result = await filterDataManager.process(response);
          return result;
        },
        enabled: searchText.length >= MINIMUM_SEARCH_INPUT,
      };
    }),
    combine: (results) => {
      const allNulls = results.every((r) => r.data === null);
      if (allNulls || searchText.length < MINIMUM_SEARCH_INPUT) {
        return { data: null, error: null, isFetching: false };
      }
      return results.reduce<{
        data: Record<string, HierarchyItem[] | null>;
        error: Error[];
        isFetching: boolean;
      }>(
        (acc, r, index) => {
          const dataFieldKey = getKeyFromDataFieldWithDataType(
            filterSections[index].dataField
          );
          const existingKey: string | undefined = Object.keys(acc.data).find(
            (k) => k === dataFieldKey
          );
          return {
            data: existingKey
              ? { ...acc.data, [existingKey]: r.data ?? null }
              : { ...acc.data, [dataFieldKey]: r.data ?? null },
            error: r.error ? [...acc.error, r.error] : acc.error,
            isFetching: acc.isFetching || r.isFetching,
          };
        },
        { data: {}, error: [], isFetching: false }
      );
    },
  });

  const filteredResult = queriesResult
    ? Object.fromEntries(
        Object.keys(queriesResult).map((key) => [
          key,
          searchItems(searchText, queriesResult[key]),
        ])
      )
    : null;
  return { data: filteredResult, error, isFetching };
};

export const useOldestStartDateQuery = (
  debugString: string,
  latestVersionId: VersionId,
  dataService: DataService
) => {
  const key = [
    `oldestStartDate, latestVersionId=${latestVersionId}`,
    hashString([...dataService.getDeps()]),
  ];
  return {
    queryKey: key,
    queryFn: () => {
      console.debug(`[useQuery] ${debugString}`, key);
      return dataService.getOldestStartDate(latestVersionId);
    },
  };
};
